import { useState, useEffect, useRef, useCallback } from 'react';
import { Platform } from 'react-native';
import {
  IosPaymentResponse,
  PaymentComplete,
  PaymentMethodNameEnum,
  PaymentRequest,
} from '@rnw-community/react-native-payments';
import useDepositSettings from './useDepositSettings';
import Config from '@betty/toolkit/config';
import { ApplePayConfig } from '../state/deposit/deposit.types';
import PaymentService from '../services/PaymentService/PaymentService';
import { logError } from '@betty/toolkit/logging';

const getApplePayToken = (paymentResponse: IosPaymentResponse) => {
  return {
    paymentMethod: {
      network: paymentResponse.details.applePayToken.paymentMethod.network,
      type: paymentResponse.details.applePayToken.paymentMethod.type,
      displayName:
        paymentResponse.details.applePayToken.paymentMethod.displayName,
    },
    transactionIdentifier:
      paymentResponse.details.applePayToken.transactionIdentifier,
    paymentData: {
      data: paymentResponse.details.applePayToken.paymentData.data,
      signature: paymentResponse.details.applePayToken.paymentData.signature,
      header: {
        publicKeyHash:
          paymentResponse.details.applePayToken.paymentData.header
            .publicKeyHash,
        ephemeralPublicKey:
          paymentResponse.details.applePayToken.paymentData.header
            .ephemeralPublicKey,
        transactionId:
          paymentResponse.details.applePayToken.paymentData.header
            .transactionId,
      },
      version: paymentResponse.details.applePayToken.paymentData.version,
    },
  };
};

export default function useApplePay(amount: number) {
  const [isAvailable, setIsAvailable] = useState<boolean | undefined>(
    Platform.select({ ios: undefined, default: false }),
  );
  const { data: depositSettings, fetch: fetchDepositSettings } =
    useDepositSettings();
  const paymentRequest = useRef<PaymentRequest | null>(null);

  const updateApplePayAvailability = useCallback(() => {
    paymentRequest.current?.canMakePayment().then(setIsAvailable);
  }, []);

  const createPaymentRequest = useCallback(
    (amount: number, applePayConfig: ApplePayConfig) => {
      paymentRequest.current = new PaymentRequest(
        [
          {
            supportedMethods: PaymentMethodNameEnum.ApplePay,
            data: applePayConfig as PaymentMethodData['data'],
          },
        ],
        {
          total: {
            amount: {
              currency: applePayConfig.currencyCode,
              value: String(amount),
            },
            label: Config.APP_NAME,
          },
        },
      );

      updateApplePayAvailability();
    },
    [updateApplePayAvailability],
  );

  const show = useCallback(async () => {
    try {
      const paymentResponse = await paymentRequest.current?.show();

      if (!paymentResponse || !paymentRequest.current) {
        return;
      }

      const data = {
        amount: Number(paymentRequest.current.details.total.amount.value),
        paymentToken: getApplePayToken(paymentResponse),
      };

      const response = await PaymentService.applePayDeposit({ data });

      if (response.status === 'Success' || response.status === 'Approved') {
        paymentResponse.complete(PaymentComplete.SUCCESS);
      } else if (
        response.status === 'Failed' ||
        response.status === 'Declined' ||
        response.status === 'Error' ||
        response.status === 'Fail'
      ) {
        paymentResponse.complete(PaymentComplete.FAIL);
      } else {
        paymentResponse.complete(PaymentComplete.UNKNOWN);
      }

      return response;
    } catch (error) {
      if (
        !(error instanceof Error) ||
        error.message !== 'Payment process canceled by user.'
      ) {
        logError('Apple Pay Error', error);
      }

      if (depositSettings?.applePayConfig) {
        createPaymentRequest(amount, depositSettings.applePayConfig);
      }
    }
  }, [amount, createPaymentRequest, depositSettings?.applePayConfig]);

  useEffect(() => {
    if (depositSettings?.applePayConfig) {
      createPaymentRequest(amount, depositSettings.applePayConfig);
    }
  }, [createPaymentRequest, amount, depositSettings]);

  useEffect(() => {
    fetchDepositSettings();
  }, [fetchDepositSettings]);

  return { isAvailable, show };
}
